import { createContext, useEffect, useState, useContext, useRef } from 'react'
import axios, { AxiosResponse } from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import useLoading from '../hooks/useLoading'
import useSnackbar from '../hooks/useSnackbar'
import useRouter from '../hooks/useRouter'

import { SessionInfo } from '../types/carbon-legacy/SessionInfo'
import { SessionCust } from '../types/carbon-legacy/SessionCust'
import { SessionJob } from '../types/carbon-legacy/SessionJob'
import { GenNode } from '../types/carbon/models/GenNode'

import {
  User,
  UserStatusResponse,
  LoginRequest,
  LoginResponse,
} from '../types/app'

import {
  API_BASE_URL,
  GOD_ROLE,
  ADMIN_ROLE,
  PLATINUM_ROLE,
} from '../constants/system'

import { getPermissionsForEmail } from '../utils/domainPermissions'
import { postLoginLanding } from '../utils/postLoginLanding'
import { extractErrorMessage } from '../utils/apitools'
import { decodeJwtActorEmail, deriveIsImpersonating } from '../utils/jwt'
import { resolveJobSwitchDestination } from '../utils/jobSwitchNavigation'
import { isMfaChallenge, submitLoginCode, type LoginReply } from '../services/mfaApi'

// Session storage key for persisting session across page reloads
const SESSION_STORAGE_KEY = 'platinum_session_info'

// localStorage key for persisting last customer/job selection
const LAST_CUSTOMER_JOB_KEY = 'platinum_last_customer_job'

// How often to check if the session is still valid (in milliseconds)
// 10 mins
// How often an idle tab polls /user/status to notice it's been superseded
// by a login from another device. Any user-initiated request also triggers
// the global axios interceptor for session_superseded, so this only matters
// when the displaced tab is doing nothing — a tight value keeps the
// licence-sharing detection window short.
const SESSION_CHECK_INTERVAL_MS = 60000

interface ILoadCustomerAndJobResponse {
  customer: SessionCust,
  job: SessionJob,
}

interface SessionStartOptions {
  successMessage: string,
  forceHomeLanding?: boolean,
}

/** Log in as / Stop impersonating met a two-step challenge (a session from before the code). */
export const MFA_RELOGIN_MESSAGE = 'Two-step login: log out and log in again with your code, then try again.'

interface IPostLoginRoute {
  name: string,
  params: Record<string, string>,
}

// LoginResult tells the login form what happened. Single-active-session
// enforcement is handled silently on the backend — a successful login here
// supersedes any previous session for the same email; the displaced browser
// is signed out on its next request with session_superseded (handled by the
// axios interceptor below).
export type LoginResult =
  | { status: 'ok' }
  | { status: 'error' }
  // Two-step login (#1724): the password was right and a code is due (or,
  // with enrol, the authenticator app must be set up first). No session yet.
  | { status: 'mfa_required', challenge: string, enrol: boolean }

export interface IAccountContext {
  initialized: boolean,
  loading: boolean,
  user?: User,
  actorEmail?: string,
  isImpersonating: boolean,
  onLogin: (username: string, password: string) => Promise<LoginResult>,
  /** Finish a login with the code (app or recovery); throws the server's refusal. */
  onLoginCode: (challenge: string, code: string, remember: boolean, name: string) => Promise<void>,
  /** Start the session a finished set-up returned (after the recovery codes were shown). */
  onCompleteLogin: (response: LoginResponse, name: string) => Promise<void>,
  onLoginAs: (managerEmail: string, managerPassword: string, targetEmail: string) => Promise<void>,
  onStopImpersonating: (managerPassword: string) => Promise<void>,
  onLogout: () => void,
  openPage: (pageName: string, params?: Record<string, string>) => void,
  openCustomerPage: (pageName: string, params?: Record<string, string>) => void,
  customer?: SessionCust,
  job?: SessionJob,
  toc: GenNode[],
  updateToc: (updater: (prev: GenNode[]) => GenNode[]) => void,
  setPostLoginRoute: (postLoginRoute: IPostLoginRoute) => void,
  setCustomerID: (customerID: string) => void,
  setJobID: (jobID: string) => void,
  syncJob: (jobName: string) => void,
  initializeJob: (customerID: string, jobID: string) => Promise<void>,
  reloadTOC: () => Promise<void>,
  addJobToSession: (customerName: string, jobName: string, jobDisplayName?: string) => void,
  addCustomerToSession: (cust: SessionCust) => void,
  replaceCustomerJobs: (customerName: string, jobs: SessionJob[]) => void,
}

export const AccountContext = createContext<IAccountContext>({
  initialized: false,
  loading: true,
  isImpersonating: false,
  toc: [],
  updateToc: () => {},
  onLogin: async () => ({ status: 'error' }),
  onLoginCode: async () => {},
  onCompleteLogin: async () => {},
  onLoginAs: async () => {},
  onStopImpersonating: async () => {},
  onLogout: () => {},
  openPage: () => {},
  openCustomerPage: () => {},
  setCustomerID: () => {},
  setPostLoginRoute: () => {},
  setJobID: () => {},
  syncJob: () => {},
  initializeJob: async () => {},
  reloadTOC: async () => {},
  addJobToSession: () => {},
  addCustomerToSession: () => {},
  replaceCustomerJobs: () => {},
})

export const useAccount = () => {
  return useContext(AccountContext)
}

const loadUserFromLocalStorage = (): User | null => {
  const storedUser = sessionStorage.getItem(SESSION_STORAGE_KEY)
  if (!storedUser) return null
  return JSON.parse(storedUser) as User
}

const saveUserToLocalStorage = (user: User) => {
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(user))
}

const clearUserFromLocalStorage = () => {
  sessionStorage.removeItem(SESSION_STORAGE_KEY)
}

interface LastCustomerJob {
  customerID: string
  jobID: string
}

const loadLastCustomerJob = (): LastCustomerJob | null => {
  const stored = localStorage.getItem(LAST_CUSTOMER_JOB_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored) as LastCustomerJob
  } catch {
    return null
  }
}

const saveLastCustomerJob = (customerID: string, jobID: string) => {
  localStorage.setItem(LAST_CUSTOMER_JOB_KEY, JSON.stringify({ customerID, jobID }))
}

const addTokenToAxios = (token: string) => {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
}

const clearTokenFromAxios = () => {
  delete axios.defaults.headers.common['Authorization']
}

// A hung goapi must not stack one open status check per poll tick. A timeout
// gets no response, so it counts as an outage and the session is kept.
const USER_STATUS_TIMEOUT_MS = 15000

const loadUserStatus = async (token: string): Promise<AxiosResponse<UserStatusResponse, any>> => {
  return axios.get<UserStatusResponse>(`${API_BASE_URL}/user/status`, {
    timeout: USER_STATUS_TIMEOUT_MS,
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
}

// buildSessionEndedMessage inspects a failed /user/status response and returns
// the user-facing message to show. The backend returns
// `{ error: "session_superseded", signed_in_at, signed_in_ip }` when another
// device has started a session for the same account; surface when and from
// where so the user can spot a suspicious login.
const buildSessionEndedMessage = (err: any): string => {
  const data = err?.response?.data
  if (data && data.error === 'session_superseded') {
    const parts: string[] = []
    if (data.signed_in_at) {
      const when = new Date(data.signed_in_at)
      if (!isNaN(when.getTime())) {
        parts.push(`at ${when.toLocaleString()}`)
      }
    }
    if (data.signed_in_ip) {
      parts.push(`from ${data.signed_in_ip}`)
    }
    const detail = parts.length ? ` (${parts.join(' ')})` : ''
    return `You were signed out because your account was signed in on another device${detail}.`
  }
  return 'Session has expired'
}

// isAuthRejection: did the server actually refuse this token? Only a 401 says
// that (invalid/expired JWT, or session_superseded). A network error (no
// response) or a 5xx — goapi restarting mid-deploy, the session lookup's DB
// being down (503 session_check_failed) — is an outage and says nothing about
// the token, so it must NOT sign the user out (#1346 item 2).
const isAuthRejection = (err: any): boolean => err?.response?.status === 401

const getCustomerAndJob = (sessionInfo: SessionInfo, customerID?: string, jobID?: string): {
  customer: SessionCust | undefined,
  job: SessionJob | undefined,
} => {
  if(!sessionInfo) return {customer: undefined, job: undefined}

  // Resolve effective ids: an explicit URL param always wins; otherwise restore
  // the last-used customer/job. This keeps param-less routes (e.g. the programme
  // tracker at /projects/:slug, which carries no :customer) on the customer the
  // user was already on after a hard refresh, instead of defaulting to the first
  // alphabetical customer.
  const last = loadLastCustomerJob()
  const effectiveCustomerID = customerID || last?.customerID || ''
  const effectiveJobID = jobID || last?.jobID || ''

  // Try to find the specified customer first (respect URL params / last-used)
  let customer = sessionInfo.sessionCusts?.find(c => c.name === effectiveCustomerID)

  // If none resolved, fall back to the first customer with jobs, or the first.
  if (!customer) {
    customer = sessionInfo.sessionCusts?.find(c => c.sessionJobs && c.sessionJobs.length > 0)
      || sessionInfo.sessionCusts?.[0]
  }
  // If customer was found by explicit customerID, keep it — even if it has no jobs (#337)

  let job = customer?.sessionJobs?.find(j => j.name === effectiveJobID)
  if(!job) job = customer?.sessionJobs?.[0]

  return {customer, job}
}

export const useAccountContext = (): IAccountContext => {
  const snackbar = useSnackbar()
  const loading = useLoading()
  const router = useRouter()
  const queryClient = useQueryClient()

  const [ initialized, setInitialized ] = useState(false)
  const [ jobLoading, setJobLoading ] = useState(false)
  const [ postLoginRoute, setPostLoginRoute] = useState<IPostLoginRoute>()

  // Ref to track the session validation interval
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  // Guard so we only handle the session_superseded kick once per session —
  // otherwise every in-flight request that 401s would queue a snackbar and a
  // navigation.
  const sessionSupersededHandledRef = useRef(false)
  const [ user, setUser ] = useState<User>()

  // we keep these as state items because then we can control
  // the exact moment they get updated i.e. after a login
  // or when the customer/job are changed
  const [ customer, setCustomer ] = useState<SessionCust>()
  const [ job, setJob ] = useState<SessionJob>()
  const [ toc, setToc ] = useState<GenNode[]>([])

  const assignUserToState = (user: User) => {
    setUser(user)
    router.setUser(user)
    saveUserToLocalStorage(user)
    addTokenToAxios(user.token)
    // Re-arm the interceptor for the new session.
    sessionSupersededHandledRef.current = false
  }

  const clearUserFromState = () => {
    setUser(undefined)
    clearUserFromLocalStorage()
    clearTokenFromAxios()
  }

  // handleSessionSuperseded runs once when any request comes back with the
  // session_superseded error code. It signs the user out and navigates home
  // with a clear explanation — see the axios interceptor below that fires
  // this on every affected response so per-feature error handlers don't
  // surface the raw "session_superseded" string.
  const handleSessionSuperseded = (err: any) => {
    if (sessionSupersededHandledRef.current) return
    sessionSupersededHandledRef.current = true
    stopSessionCheck()
    const loggedOutMessage = buildSessionEndedMessage(err)
    snackbar.error(loggedOutMessage)
    clearUserFromState()
    router.navigate('home', { loggedOutMessage })
  }

  const stopSessionCheck = () => {
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current)
      sessionCheckIntervalRef.current = null
    }
  }

  const startSessionCheck = (user: User) => {
    // Clear any existing interval first
    stopSessionCheck()

    // Start a new interval to check session validity
    sessionCheckIntervalRef.current = setInterval(async () => {
      if (!user?.token) {
        stopSessionCheck()
        return
      }

      try {
        await loadUserStatus(user.token)
      } catch (e) {
        const message = extractErrorMessage(e)
        if (!isAuthRejection(e)) {
          // An outage, not a logout: keep the session and the interval, and
          // ask again on the next tick.
          console.warn('Session check could not reach the server; retrying:', message)
          return
        }
        console.error('Session validation failed:', message)

        // Stop the interval
        stopSessionCheck()

        // Detect the single-active-session kick from the backend so we can
        // explain to the user why they were signed out.
        const loggedOutMessage = buildSessionEndedMessage(e)

        // Show error snackbar
        snackbar.error(loggedOutMessage)

        // Clear user state
        clearUserFromState()

        // Redirect to home with logged out message
        router.navigate('home', {
          loggedOutMessage,
        })
      }
    }, SESSION_CHECK_INTERVAL_MS)
  }

  const loadCustomerAndJob = async (user: User, customerID?: string, jobID?: string): Promise<ILoadCustomerAndJobResponse | undefined> => {
    const {
      customer,
      job,
    } = getCustomerAndJob(user.session_info, customerID, jobID)
    // A Manager may open a customer with no jobs yet (a new one), so they can
    // reach its Jobs page and make the first; there is no job to select.
    if(customer && !job && user.role_permissions?.isManager) {
      setCustomer(customer)
      setJob(undefined)
      setToc([])
      return
    }
    if(!customer || !job) {
      if(customer && !job) {
        snackbar.error('No jobs found for that customer')
      }
      console.error(`no customer or job found`, {
        customerID,
        jobID,
        customer,
        job,
      })
      return
    }
    console.log(`selecting customer and job`, {
      customerID,
      jobID: job.name,
      customer,
      job,
    })

    // Just update context state - no API call needed
    // TOC will be loaded lazily by Tables page via reloadTOC()
    setCustomer(customer)
    setJob(job)
    setToc([]) // Clear TOC - will be loaded when Tables page mounts
    saveLastCustomerJob(customer.name || '', job.name || '')

    return {
      customer,
      job,
    }
  }

  const initializeJob = async (customerID: string, jobID: string) => {
    if(!user) {
      snackbar.error('No user found')
      return
    }
    await loadCustomerAndJob(user, customerID, jobID)
  }

  const reloadTOC = async () => {
    if (!customer || !job) {
      console.warn('reloadTOC called without customer/job')
      return
    }
    try {
      const res = await axios.get<{ toc: GenNode[] }>(`${API_BASE_URL}/job/${customer.name}/${job.name}/toc`)
      setToc(res.data.toc || [])
    } catch (e) {
      console.error('Failed to reload TOC:', e)
      snackbar.error('Failed to load table of contents')
    }
  }

  const navigateToNewJob = (data: ILoadCustomerAndJobResponse) => {
    // Stay in whichever top-level area the user is in (DataViz, Tables, Files,
    // etc.) rather than always bouncing to Tables (#962 Obs 1). A job-scoped
    // entity open in the old job (e.g. a specific dashboard) can't survive the
    // switch, so this always lands on the area's home/list route. TikTok jobs
    // override this and land on the Sky product chooser (T6.2) — the resolver
    // owns that rule too.
    const destination = resolveJobSwitchDestination(router.name, data.job.name || '')
    router.navigate(destination.routeName, {
      customer: data.customer.name,
      ...(destination.needsJob ? { job: data.job.name } : {}),
    })
  }

  // this is called both by the initialize method and the onLogin method
  const handleUserLoaded = (user: User) => {
    assignUserToState(user)
    startSessionCheck(user)
  }

  const setCustomerID = async (customerID: string) => {
    if(!user) {
      snackbar.error('No user found')
      return
    }
    loading.setLoading(true)
    setJobLoading(true)
    try {
      const loadedJob = await loadCustomerAndJob(user, customerID, '')
      if(loadedJob) {
        navigateToNewJob(loadedJob)
      } else if(user.role_permissions?.isManager) {
        const target = user.session_info?.sessionCusts?.find(c => c.name === customerID)
        if(target && !target.sessionJobs?.length) {
          snackbar.info(`${target.displayName || target.name} has no jobs yet. Make the first one here.`)
          router.navigate('admin-jobs', { customer: target.name })
        }
      }
    } catch(e) {
      snackbar.error(extractErrorMessage(e))
      console.error('Error setting customer ID:', e)
    } finally {
      loading.setLoading(false)
      setJobLoading(false)
    }
  }
  
  const syncJob = (jobName: string) => {
    if (!user || !customer?.name) return
    loadCustomerAndJob(user, customer.name, jobName)
  }

  const setJobID = async (jobID: string) => {
    if(!user) {
      snackbar.error('No user found')
      return
    }
    if(!customer || !customer.name) {
      snackbar.error('No customer found')
      return
    }
    loading.setLoading(true)
    setJobLoading(true)
    try {
      const loadedJob = await loadCustomerAndJob(user, customer.name, jobID)
      if(loadedJob) {
        navigateToNewJob(loadedJob)
      }
    } catch(e) {
      snackbar.error(extractErrorMessage(e))
      console.error('Error setting customer ID:', e)
    } finally {
      loading.setLoading(false)
      setJobLoading(false)
    }
  }

  // Main initialization sequence - runs once on mount
  const initialize = async (): Promise<void> => {
    const storedUser = loadUserFromLocalStorage()
    if (storedUser) {
      let rejection: unknown = undefined
      try {
        await loadUserStatus(storedUser.token)
        console.log('found user in local storage', storedUser)
      } catch (e) {
        // Only a 401 means the cached login is invalid. On a network error or
        // a 5xx keep it: the server still checks the token on every request,
        // and the 60s poll signs out if a later check gets a 401.
        if (isAuthRejection(e)) {
          rejection = e
        } else {
          console.warn('Could not check the stored session; keeping it:', extractErrorMessage(e))
        }
      }
      if (!rejection) {
        // Recompute domain permissions on restore so new permission flags take effect
        // without requiring a re-login
        storedUser.domain_permissions = getPermissionsForEmail(
          storedUser.session_info?.email || storedUser.session_info?.name || '',
          storedUser.session_info?.roles || []
        )
        // Re-derive actor_email from the stored token so refreshes preserve
        // impersonation state even if older clients omitted it from sessionStorage.
        if (!storedUser.actor_email) {
          storedUser.actor_email = decodeJwtActorEmail(storedUser.token)
        }
        handleUserLoaded(storedUser)
      } else {
        const e = rejection
        console.error('Error loading user status:', extractErrorMessage(e))
        // If the reload failed because another device took over the session,
        // mention that on the login screen rather than silently signing out.
        const supersededMsg = (e as any)?.response?.data?.error === 'session_superseded'
          ? buildSessionEndedMessage(e)
          : null
        clearUserFromState()
        if (supersededMsg) {
          snackbar.error(supersededMsg)
        }
      }
    } else {
      clearUserFromState()
      if(router.name && router.name !== 'home' && router.meta?.public !== true) {
        setPostLoginRoute({
          name: router.name,
          params: router.params,
        })
        router.navigate('home')
      }
    }
    loading.setLoading(false)
    setInitialized(true)
  }

  // performLogin runs the shared login plumbing — hitting /user/login with the
  // given request body, stamping the session into state, and routing to the
  // right landing page. onLogin / onLoginAs / onStopImpersonating differ only
  // in the request body and the snackbar wording.
  // It returns the challenge instead when the account's two-step login is due
  // (#1724); the caller asks for the code and startSession runs after it.
  const performLogin = async (
    requestBody: LoginRequest,
    opts: SessionStartOptions,
  ) => {
    const loginResponse = await axios.post<LoginReply>(`${API_BASE_URL}/user/login`, requestBody)
    if (isMfaChallenge(loginResponse.data)) return loginResponse.data
    await startSession(loginResponse.data, opts)
    return undefined
  }

  // startSession stamps a login's session into state and routes to the
  // landing page: straight after the password, or after the two-step code.
  const startSession = async (
    loginData: LoginResponse,
    {
      successMessage,
      // For impersonation transitions (login-as, stop-impersonating) we want a
      // clean landing rather than restoring the previous customer/job/route.
      forceHomeLanding = false,
    }: SessionStartOptions,
  ) => {
    const loginResponse = { data: loginData }
    // A new identity (a login, Log in as, Stop impersonating) must not read the
    // previous one's cached answers: /me, for one, is cached per customer, so
    // after Log in as the page kept the Manager's "no budget applies" for a
    // minute and the member's budget banner never showed (6 Oct). onLogout
    // clears the cache for the same reason.
    queryClient.clear()
    const statusResponse = await loadUserStatus(loginResponse.data.token)

    const session_info = loginResponse.data.session_info
    session_info.roles = (session_info.roles || []).map(role => role.toLowerCase())

    const isManager = session_info.roles?.includes(GOD_ROLE.toLowerCase()) || false
    const isRealmAdmin = session_info.roles?.includes(ADMIN_ROLE.toLowerCase()) || false
    const hasPlatinumRole = session_info.roles?.includes(PLATINUM_ROLE.toLowerCase()) || false

    const actorEmail = decodeJwtActorEmail(loginResponse.data.token)

    // Domain permissions should follow the *effective* session identity — for
    // impersonation that's the target (session_info.email), not the manager.
    const effectiveEmail = session_info.email || session_info.name || ''

    const loggedInUser: User = {
      token: loginResponse.data.token,
      session_info,
      session_status: statusResponse.data.session_status,
      domain_permissions: getPermissionsForEmail(effectiveEmail, session_info.roles),
      role_permissions: {
        isManager,
        isRealmAdmin: isManager || isRealmAdmin,
        hasPlatinumRole: isManager || isRealmAdmin || hasPlatinumRole,
      },
      actor_email: actorEmail,
    }

    console.log(`user logged in`, loggedInUser)
    handleUserLoaded(loggedInUser)
    // Always gone within 2s — unconditionally, no pause on hover or blur.
    snackbar.success(successMessage, { autoHideMs: 2000 })

    let loadedJob: ILoadCustomerAndJobResponse | undefined
    try {
      let targetCustomerID = ''
      let targetJobID = ''

      if (!forceHomeLanding) {
        targetCustomerID = postLoginRoute?.params.customer || ''
        targetJobID = postLoginRoute?.params.job || ''
        if (!targetCustomerID && !targetJobID) {
          const lastSelection = loadLastCustomerJob()
          if (lastSelection) {
            targetCustomerID = lastSelection.customerID
            targetJobID = lastSelection.jobID
          }
        }
      }

      loadedJob = await loadCustomerAndJob(loggedInUser, targetCustomerID, targetJobID)
    } catch (jobError) {
      console.error('Failed to load customer/job after login:', jobError)
    }

    let landing: ReturnType<typeof postLoginLanding>
    if (!forceHomeLanding && postLoginRoute?.name) {
      router.navigate(postLoginRoute.name, postLoginRoute.params)
      setPostLoginRoute(undefined)
    } else if ((landing = postLoginLanding(!!loggedInUser.domain_permissions?.canAccessAgent, loadedJob?.customer?.name, loadedJob?.job?.name))) {
      router.navigate(landing.name, landing.params)
    } else if (loadedJob?.customer?.name) {
      router.navigate('landing', { customer: loadedJob.customer.name })
    } else {
      snackbar.error('No jobs available for your account')
      router.navigate('home')
    }
  }

  // Standard manager-or-user login. Single-active-session enforcement is
  // handled silently on the backend — this call always supersedes any
  // previous session for the same email, and the displaced browser is
  // signed out on its next request with session_superseded.
  const onLogin = async (name: string, password: string): Promise<LoginResult> => {
    loading.setLoading(true)
    try {
      const challenge = await performLogin({ name, password }, { successMessage: `Logged in as ${name}` })
      if (challenge) return { status: 'mfa_required', challenge: challenge.challenge, enrol: challenge.enrol }
      return { status: 'ok' }
    } catch (e) {
      const message = extractErrorMessage(e)
      snackbar.error(message)
      console.error('Error logging in:', e)
      clearUserFromState()
      return { status: 'error' }
    } finally {
      loading.setLoading(false)
    }
  }

  // The two-step code: the session on success. A refusal (wrong code, expired
  // challenge, too many tries) is thrown for the login page to word.
  const onLoginCode = async (challenge: string, code: string, remember: boolean, name: string) => {
    loading.setLoading(true)
    try {
      const data = await submitLoginCode(challenge, code, remember)
      await startSession(data, { successMessage: `Logged in as ${name}` })
    } finally {
      loading.setLoading(false)
    }
  }

  const onCompleteLogin = async (response: LoginResponse, name: string) => {
    loading.setLoading(true)
    try {
      await startSession(response, { successMessage: `Logged in as ${name}` })
    } catch (e) {
      snackbar.error(extractErrorMessage(e))
      clearUserFromState()
    } finally {
      loading.setLoading(false)
    }
  }

  // Manager impersonation: authenticate with the manager's credentials but
  // start a session as the target user. Gated server-side on the licensing
  // Impersonate role; Platinum surfaces the specific 403 body "impersonation_denied".
  const onLoginAs = async (managerEmail: string, managerPassword: string, targetEmail: string) => {
    loading.setLoading(true)
    try {
      const challenge = await performLogin(
        { name: managerEmail, password: managerPassword, impersonate_email: targetEmail },
        { successMessage: `Logged in as ${targetEmail}`, forceHomeLanding: true },
      )
      // Your own session normally stands in for your two-step code; a session
      // from before you set it up does not.
      if (challenge) snackbar.error(MFA_RELOGIN_MESSAGE)
    } catch (e) {
      const message = extractErrorMessage(e)
      snackbar.error(message === 'impersonation_denied'
        ? 'You do not have permission to impersonate this user'
        : message)
      console.error('Error impersonating:', e)
      // Don't clear the manager's existing session on a failed impersonation —
      // their original login is still valid.
    } finally {
      loading.setLoading(false)
    }
  }

  // Stop impersonating: re-authenticate as the manager identified in the
  // current token's actor_email claim. A password re-prompt protects against
  // a walk-up attacker using a stale impersonated session.
  const onStopImpersonating = async (managerPassword: string) => {
    const managerEmail = user?.actor_email
    if (!managerEmail) {
      snackbar.error('Not currently impersonating a user')
      return
    }
    loading.setLoading(true)
    try {
      const challenge = await performLogin(
        { name: managerEmail, password: managerPassword },
        { successMessage: `Logged back in as ${managerEmail}`, forceHomeLanding: true },
      )
      if (challenge) snackbar.error(MFA_RELOGIN_MESSAGE)
    } catch (e) {
      const message = extractErrorMessage(e)
      snackbar.error(message)
      console.error('Error stopping impersonation:', e)
    } finally {
      loading.setLoading(false)
    }
  }

  const onLogout = async () => {
    // Stop session validation check
    stopSessionCheck()

    // Clear React Query cache to prevent data leakage between users
    queryClient.clear()

    try {
      await axios.post(`${API_BASE_URL}/user/logout`)
    } catch(e) {

    }
    clearUserFromState()
    snackbar.success('Logged out')
    router.navigate('home')
  }

  const openPage = (name: string, params: Record<string, string> = {}) => {
    if(!customer || !job) {
      snackbar.error('No customer or job selected')
      return
    }
    router.navigate(name, {
      customer: customer?.name,
      job: job?.name,
      ...params,
    })
  }

  const openCustomerPage = (name: string, params: Record<string, string> = {}) => {
    if(!customer) {
      snackbar.error('No customer selected')
      return
    }
    router.navigate(name, {
      customer: customer?.name,
      ...params,
    })
  }

  useEffect(() => {
    initialize()

    // Global axios response interceptor for session_superseded. Any request —
    // table load, /user/status poll, agent SSE, anything — that comes back
    // with 401 + this error code means the backend has replaced this user's
    // session with a newer one (login on another device). We sign out and
    // navigate to home with the reason, and swallow the error so no
    // per-feature "Failed to load X" snackbar fires on top.
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const data = error?.response?.data
        if (error?.response?.status === 401 && data?.error === 'session_superseded') {
          handleSessionSuperseded(error)
          // Return a pending promise so callers don't render their own error
          // UI for this request — the sign-out + redirect is the only action.
          return new Promise(() => {})
        }
        return Promise.reject(error)
      },
    )

    // Cleanup: stop session check on unmount
    return () => {
      stopSessionCheck()
      axios.interceptors.response.eject(interceptorId)
    }
  }, [])

  // addJobToSession injects a newly created job into the session state
  // so it appears in the job dropdown without requiring re-login
  const addJobToSession = (customerName: string, jobName: string, jobDisplayName?: string) => {
    if (!user) return
    const sessionInfo = user.session_info
    if (!sessionInfo?.sessionCusts) return

    const cust = sessionInfo.sessionCusts.find(c => c.name === customerName)
    if (!cust) return

    // Check if job already exists
    if (cust.sessionJobs?.some(j => j.name === jobName)) return

    const newJob: SessionJob = {
      name: jobName,
      displayName: jobDisplayName || jobName,
    }
    cust.sessionJobs = [...(cust.sessionJobs || []), newJob]

    // Update user in state and sessionStorage
    const updatedUser = { ...user, session_info: { ...sessionInfo } }
    assignUserToState(updatedUser)

    // If this is the current customer, update customer state too
    if (customer?.name === customerName) {
      setCustomer({ ...cust })
    }
  }

  // addCustomerToSession adds a customer a Manager has just made to the
  // session's customer list, so it is in the customer menu without logging
  // out and in. The next login reads the list from licensing as before.
  const addCustomerToSession = (cust: SessionCust) => {
    if (!user || !cust.name) return
    const sessionInfo = user.session_info
    const custs = sessionInfo?.sessionCusts || []
    if (custs.some(c => c.name === cust.name)) return
    const updatedUser = { ...user, session_info: { ...sessionInfo, sessionCusts: [...custs, { ...cust, sessionJobs: cust.sessionJobs || [] }] } }
    assignUserToState(updatedUser)
  }

  // replaceCustomerJobs puts the server's job list for one customer into the
  // session after a job is made, renamed or deleted, so the job menu matches
  // licensing without logging out and in. On the current customer, a job that
  // has gone (or none at all) gives way to the first one, so pages that need
  // a job have one.
  const replaceCustomerJobs = (customerName: string, jobs: SessionJob[]) => {
    if (!user) return
    const sessionInfo = user.session_info
    const custs = sessionInfo?.sessionCusts || []
    const current = custs.find(c => c.name === customerName)
    if (!current) return
    const updatedCust = { ...current, sessionJobs: jobs }
    assignUserToState({ ...user, session_info: { ...sessionInfo, sessionCusts: custs.map(c => (c === current ? updatedCust : c)) } })
    if (customer?.name !== customerName) return
    setCustomer(updatedCust)
    const next = jobs.find(j => j.name === job?.name) || jobs[0]
    if (next?.name !== job?.name) setToc([])
    setJob(next)
    if (next?.name) saveLastCustomerJob(customerName, next.name)
  }

  const actorEmail = user?.actor_email
  const effectiveEmail = user?.session_info?.email || user?.session_info?.name || ''
  const isImpersonating = deriveIsImpersonating(actorEmail, effectiveEmail)

  return {
    initialized,
    loading: jobLoading,
    user,
    actorEmail,
    isImpersonating,
    onLogin,
    onLoginCode,
    onCompleteLogin,
    onLoginAs,
    onStopImpersonating,
    onLogout,
    reloadTOC,
    openPage,
    openCustomerPage,
    setPostLoginRoute,
    customer,
    job,
    toc,
    updateToc: setToc,
    setCustomerID,
    setJobID,
    syncJob,
    initializeJob,
    addJobToSession,
    addCustomerToSession,
    replaceCustomerJobs,
  }
}

export const AccountContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const value = useAccountContext()
  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  )
}